/**
 * Доска объявлений — раздел ЛК.
 *
 * В legacy это была заглушка «Раздел в разработке» (main_bulletinboard.tpl), копировать
 * нечего — раздел написан заново. Вёрстка на легаси-классах (Bootstrap 3 + main.css),
 * своих CSS-классов не вводим (CLAUDE.md п.9).
 *
 * Объявление подаёт житель; своё он правит и снимает с доски сам. Снятое АДМИНОМ вернуть
 * не может — об этом честно пишем в карточке.
 *
 * Фото лежат в БД и закрыты авторизацией, поэтому в <img> идёт не путь, а blob-ссылка
 * (useAuthImage): браузер, загружая картинку, заголовок Authorization не шлёт.
 *
 * API: GET/POST /api/board, GET/PUT /api/board/{id}, POST /api/board/{id}/publish|unpublish,
 *      POST/DELETE /api/board/{id}/photos, GET /board/photo/{id}.
 */

import { useEffect, useState, type FormEvent } from 'react'

import { api, ApiError } from '@/api/client'
import type { BoardAd } from '@/api/types'
import { Modal } from '@/components/Modal'
import { useApi } from '@/lib/useApi'
import { useAuthImage } from '@/lib/useAuthImage'

/** Вкладки ленты. mine — свои объявления, включая снятые с доски. */
type Filter = 'all' | 'service' | 'goods' | 'mine'

const FILTERS: ReadonlyArray<{ key: Filter; label: string }> = [
  { key: 'all', label: 'Все' },
  { key: 'service', label: 'Услуги' },
  { key: 'goods', label: 'Товары' },
  { key: 'mine', label: 'Мои объявления' },
]

export const MAX_PHOTOS = 10

/**
 * Стоимость — свободный текст, который человек написал сам («от 1000 р. за кв/м»).
 * Ничего к нему не дописываем: «руб.» превратило бы это в «от 1000 р. за кв/м руб.».
 * Пусто → «Договорная».
 */
export function priceLabel(price: string | null): string {
  const text = price?.trim() ?? ''
  return text === '' ? 'Договорная' : text
}

function pathFor(filter: Filter): string {
  if (filter === 'mine') return '/api/board?mine=1'
  if (filter === 'all') return '/api/board'
  return `/api/board?kind=${filter}`
}

export function BulletinBoardPage() {
  const [filter, setFilter] = useState<Filter>('all')
  const { data, loading, reload } = useApi<BoardAd[]>(pathFor(filter))

  const [editing, setEditing] = useState<number | 'new' | null>(null)
  const [opened, setOpened] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const ads = data ?? []

  const act = async (path: string): Promise<void> => {
    setError(null)
    try {
      await api.post(path)
      reload()
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Не удалось выполнить действие')
    }
  }

  /** Удалить своё объявление насовсем (мягко на бэке). Спрашиваем — действие необратимо для жителя. */
  const remove = async (id: number): Promise<void> => {
    if (!window.confirm('Удалить объявление? Оно исчезнет с доски и из «Моих» — вернуть будет нельзя.')) {
      return
    }
    setError(null)
    try {
      await api.delete(`/api/board/${id}`)
      reload()
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Не удалось удалить объявление')
    }
  }

  return (
    <div className="row">
      <div className="col-md-12">
        <div style={{ paddingTop: 20, paddingBottom: 10, display: 'flex', alignItems: 'center' }}>
          <ul className="nav nav-pills" style={{ flex: 1 }}>
            {FILTERS.map((item) => (
              <li key={item.key} className={filter === item.key ? 'active' : undefined}>
                <a
                  href={`#${item.key}`}
                  onClick={(event) => {
                    event.preventDefault()
                    setFilter(item.key)
                  }}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
          <button type="button" className="btn btn-success" onClick={() => setEditing('new')}>
            Подать объявление
          </button>
        </div>

        {error !== null && <div className="alert alert-danger">{error}</div>}

        {loading && ads.length === 0 && <p className="text-muted">Загрузка…</p>}

        {!loading && ads.length === 0 && (
          <p className="text-muted" style={{ paddingTop: 20 }}>
            {filter === 'mine' ? 'Вы ещё не подавали объявлений.' : 'Объявлений пока нет.'}
          </p>
        )}

        <div className="row">
          {ads.map((ad) => (
            <div className="col-sm-6 col-md-4" key={ad.id}>
              <AdCard
                ad={ad}
                onOpen={() => setOpened(ad.id)}
                onEdit={() => setEditing(ad.id)}
                onUnpublish={() => void act(`/api/board/${ad.id}/unpublish`)}
                onPublish={() => void act(`/api/board/${ad.id}/publish`)}
                onDelete={() => void remove(ad.id)}
              />
            </div>
          ))}
        </div>
      </div>

      {editing !== null && (
        <AdFormModal
          adId={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            reload()
          }}
        />
      )}

      {opened !== null && <AdViewModal adId={opened} onClose={() => setOpened(null)} />}
    </div>
  )
}

/** Карточка в ленте: обложка, тип, название, цена, участок. */
function AdCard({
  ad,
  onOpen,
  onEdit,
  onUnpublish,
  onPublish,
  onDelete,
}: {
  ad: BoardAd
  onOpen: () => void
  onEdit: () => void
  onUnpublish: () => void
  onPublish: () => void
  onDelete: () => void
}) {
  const cover = useAuthImage(ad.cover_id === null ? null : `/board/photo/${ad.cover_id}`)
  const hiddenByAdmin = !ad.is_published && ad.hidden_by === 'admin'

  return (
    <div className="thumbnail" style={{ minHeight: 330 }}>
      <div
        style={{
          height: 160,
          background: cover === null ? '#f5f5f5' : `url(${cover}) center / cover no-repeat`,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onClick={onOpen}
      >
        {cover === null && <span className="text-muted">без фото</span>}
      </div>

      <div className="caption">
        <p>
          <span className="label label-default">{ad.kind_label}</span>{' '}
          {!ad.is_published && (
            <span className="label label-warning">
              {hiddenByAdmin ? 'снято администратором' : 'снято с публикации'}
            </span>
          )}
        </p>

        <h4 style={{ cursor: 'pointer' }} onClick={onOpen}>
          {ad.title}
        </h4>
        <p>
          <b>{priceLabel(ad.price)}</b>
        </p>
        {ad.photo_count > 1 && <p className="text-muted">Фото: {ad.photo_count}</p>}

        {ad.mine && (
          <p>
            <button type="button" className="btn btn-default btn-sm" onClick={onEdit}>
              Изменить
            </button>{' '}
            {ad.is_published ? (
              <button type="button" className="btn btn-default btn-sm" onClick={onUnpublish}>
                Снять с публикации
              </button>
            ) : (
              // Снятое админом вернуть нельзя — кнопки нет, вместо неё пояснение ниже.
              !hiddenByAdmin && (
                <button type="button" className="btn btn-default btn-sm" onClick={onPublish}>
                  Вернуть на доску
                </button>
              )
            )}{' '}
            {/* Удаление — совсем убрать объявление (в отличие от «снять с публикации»). */}
            <button type="button" className="btn btn-danger btn-sm" onClick={onDelete}>
              Удалить
            </button>
          </p>
        )}

        {ad.mine && hiddenByAdmin && (
          <p className="text-muted">
            <small>Объявление снято администратором — обратитесь в правление.</small>
          </p>
        )}
      </div>
    </div>
  )
}

/** Просмотр: все фото, описание и контакт. */
function AdViewModal({ adId, onClose }: { adId: number; onClose: () => void }) {
  const { data } = useApi<BoardAd>(`/api/board/${adId}`)
  const [current, setCurrent] = useState(0)

  const photos = data?.photos ?? []
  const shown = photos[current] ?? null
  const big = useAuthImage(shown === null ? null : `/board/photo/${shown}`)

  return (
    <Modal open onClose={onClose} title={data?.title ?? 'Объявление'} size="lg">
      {data === null && <p className="text-muted">Загрузка…</p>}

      {data !== null && (
        <>
          {/*
            Область показа фиксированной высоты, фото вписывается внутрь (object-fit: contain).
            Иначе окно прыгало: вертикальный снимок занимал все 380 px, горизонтальный — вдвое
            меньше, и при переключении модалка дёргалась. Держим коробку постоянной и когда
            фото ещё грузится — тогда не прыгает и на первой отрисовке.
          */}
          {photos.length > 0 && (
            <div
              style={{
                height: 380,
                marginBottom: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                // Подложки нет намеренно: у фото любых пропорций по бокам оставались бы
                // серые поля, а это выглядит как брак вёрстки. Коробка фиксированной
                // высоты держит окно от прыжков и без заливки.
              }}
            >
              {big !== null && (
                <img
                  src={big}
                  alt={data.title}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              )}
            </div>
          )}

          {/*
            Переключатель — миниатюры самих фото, а не номера: по цифре «3» человек не
            понимает, что он выберет. Выбранная обведена рамкой.
          */}
          {photos.length > 1 && (
            <p style={{ textAlign: 'center' }}>
              {photos.map((photoId, index) => (
                <PreviewButton
                  key={photoId}
                  photoId={photoId}
                  active={index === current}
                  onClick={() => setCurrent(index)}
                />
              ))}
            </p>
          )}

          <p>
            <span className="label label-default">{data.kind_label}</span> <b>{priceLabel(data.price)}</b>
          </p>

          {data.description !== '' && <p style={{ whiteSpace: 'pre-wrap' }}>{data.description}</p>}

          {/* Одна строка «Контакт: …» — таблица ради одной пары полей только сдвигала её от края */}
          <p>
            <b>Контакт:</b> <ContactLink contact={data.contact} />
          </p>
        </>
      )}
    </Modal>
  )
}

/**
 * Контакт объявления — ссылка, по которой можно сразу позвонить или написать.
 *
 * Поле свободное: человек пишет «+7 (911) 000-00-00», «89110000000», «ivan@mail.ru»
 * или «звоните после 18, Иван». Поэтому решаем по содержимому:
 *   • есть «@» и точка после неё → почта, mailto:;
 *   • набралось 10–15 цифр → телефон, tel: (в tel: только цифры и плюс — скобки и
 *     дефисы часть телефонов ломают);
 *   • всё остальное — просто текст, ссылку не выдумываем.
 */
function ContactLink({ contact }: { contact: string }) {
  const text = contact.trim()
  if (text === '') return <span className="text-muted">не указан</span>

  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text)) {
    return <a href={`mailto:${text}`}>{text}</a>
  }

  const digits = text.replace(/\D/g, '')
  if (digits.length >= 10 && digits.length <= 15) {
    // Ведущий плюс сохраняем, если он был: «+7…» и «8…» — разные номера.
    const tel = text.trim().startsWith('+') ? `+${digits}` : digits
    return <a href={`tel:${tel}`}>{text}</a>
  }

  return <>{text}</>
}

/** Миниатюра-переключатель в окне просмотра: выбранная обведена зелёной рамкой. */
function PreviewButton({
  photoId,
  active,
  onClick,
}: {
  photoId: number
  active: boolean
  onClick: () => void
}) {
  const url = useAuthImage(`/board/photo/${photoId}`)

  return (
    <button
      type="button"
      onClick={onClick}
      title="Показать это фото"
      style={{
        padding: 0,
        marginRight: 6,
        marginBottom: 6,
        lineHeight: 0,
        background: 'none',
        cursor: 'pointer',
        // Выбранная миниатюра — заметная рамка, остальные приглушены.
        border: active ? '2px solid #5cb85c' : '2px solid #ddd',
        opacity: active ? 1 : 0.65,
      }}
    >
      <img
        src={url ?? undefined}
        alt=""
        style={{ width: 64, height: 64, objectFit: 'cover', display: 'block' }}
      />
    </button>
  )
}

/**
 * Форма подачи и правки.
 *
 * Фото привязываются к id объявления, а у нового объявления его ещё нет. Поэтому при
 * подаче выбранные файлы держатся В БРАУЗЕРЕ (с превью), а на сервер уходят сразу после
 * того, как объявление создано, — одним запросом. Для человека это выглядит как «выбрал
 * фото и нажал Сохранить»; лишнего шага «сначала сохраните, потом добавьте фото» нет.
 *
 * При правке существующего объявления id уже известен, поэтому фото грузятся сразу
 * по выбору файла.
 */
function AdFormModal({
  adId,
  onClose,
  onSaved,
}: {
  adId: number | null
  onClose: () => void
  onSaved: () => void
}) {
  const { data, reload } = useApi<BoardAd>(adId === null ? null : `/api/board/${adId}`)

  const [kind, setKind] = useState<'service' | 'goods'>('goods')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [price, setPrice] = useState('')
  const [contact, setContact] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [filled, setFilled] = useState(adId === null)

  /** Фото нового объявления: файлы ждут в браузере, пока объявление не создано. */
  const [pending, setPending] = useState<File[]>([])

  // Существующее объявление догрузилось — заполняем поля один раз.
  if (data !== null && !filled) {
    setKind(data.kind === 'service' ? 'service' : 'goods')
    setTitle(data.title)
    setDescription(data.description)
    setPrice(data.price ?? '')
    setContact(data.contact)
    setFilled(true)
  }

  const photos = data?.photos ?? []

  /** Отправить файлы к уже существующему объявлению. Возвращает текст ошибки или null. */
  const sendPhotos = async (id: number, files: File[]): Promise<string | null> => {
    if (files.length === 0) return null

    const form = new FormData()
    for (const file of files) form.append('photo[]', file)

    const result = await api.upload<{ saved: number; errors: string[] }>(
      `/api/board/${id}/photos`,
      form,
    )
    return result.errors.length > 0 ? result.errors.join(' ') : null
  }

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const body = { kind, title, description, price, contact }

      if (adId === null) {
        const created = await api.post<{ id: number }>('/api/board', body)

        // Объявление создано — теперь у фото есть к чему привязаться.
        const photoError = await sendPhotos(created.id, pending)
        if (photoError !== null) {
          // Само объявление уже на доске, поэтому не откатываем его, а честно говорим,
          // что часть фото не взялась: остальное человек доложит правкой.
          setError(`Объявление опубликовано, но с фото беда: ${photoError}`)
          setBusy(false)
          return
        }
      } else {
        await api.put(`/api/board/${adId}`, body)
      }

      onSaved()
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Не удалось сохранить объявление')
    } finally {
      setBusy(false)
    }
  }

  /** Сколько фото уже есть: у нового объявления это выбранные файлы, у старого — загруженные. */
  const photoCount = adId === null ? pending.length : photos.length

  const pick = async (files: FileList | null): Promise<void> => {
    if (files === null || files.length === 0) return
    setError(null)

    // Лишнее сверх лимита не берём: сервер всё равно отвергнет, но лучше сказать сразу.
    const free = MAX_PHOTOS - photoCount
    const chosen = Array.from(files).slice(0, Math.max(0, free))
    if (chosen.length < files.length) {
      setError(`Можно не больше ${MAX_PHOTOS} фото — лишние не добавлены.`)
    }
    if (chosen.length === 0) return

    // Новое объявление ещё не создано — файлам не к чему привязаться, держим их у себя.
    if (adId === null) {
      setPending((list) => [...list, ...chosen])
      return
    }

    try {
      const photoError = await sendPhotos(adId, chosen)
      if (photoError !== null) setError(photoError)
      reload()
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Не удалось загрузить фото')
    }
  }

  const removePhoto = async (photoId: number): Promise<void> => {
    if (adId === null) return
    try {
      await api.delete(`/api/board/${adId}/photos/${photoId}`)
      reload()
    } catch {
      setError('Не удалось удалить фото')
    }
  }

  return (
    <Modal open onClose={onClose} title={adId === null ? 'Подать объявление' : 'Изменить объявление'}>
      <form onSubmit={(event) => void submit(event)}>
        {error !== null && <div className="alert alert-danger">{error}</div>}

        <div className="form-group">
          <label>Тип</label>
          <select
            className="form-control"
            value={kind}
            onChange={(event) => setKind(event.target.value === 'service' ? 'service' : 'goods')}
          >
            <option value="goods">Товар</option>
            <option value="service">Услуга</option>
          </select>
        </div>

        <div className="form-group">
          <label>Название</label>
          <input
            type="text"
            className="form-control"
            value={title}
            maxLength={255}
            required
            onChange={(event) => setTitle(event.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Описание</label>
          <textarea
            className="form-control"
            rows={4}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Стоимость</label>
          <input
            type="text"
            className="form-control"
            value={price}
            placeholder="Например: 5000 или «от 1000 р. за кв/м». Пусто — «Договорная»"
            onChange={(event) => setPrice(event.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Контакт</label>
          <input
            type="text"
            className="form-control"
            value={contact}
            maxLength={255}
            placeholder="Телефон или как с вами связаться"
            onChange={(event) => setContact(event.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Фото (не больше {MAX_PHOTOS})</label>

          <div style={{ marginBottom: 8 }}>
            {/* Уже загруженные (правка объявления) */}
            {photos.map((photoId) => (
              <PhotoThumb key={photoId} photoId={photoId} onRemove={() => void removePhoto(photoId)} />
            ))}

            {/* Выбранные, но ещё не отправленные: уйдут сразу после создания объявления */}
            {pending.map((file, index) => (
              <LocalThumb
                key={`${file.name}-${file.size}-${index}`}
                file={file}
                onRemove={() => setPending((list) => list.filter((_, i) => i !== index))}
              />
            ))}

            {photoCount === 0 && (
              <p className="text-muted">
                <small>Фото пока нет.</small>
              </p>
            )}
          </div>

          <input
            type="file"
            accept="image/*"
            multiple
            disabled={photoCount >= MAX_PHOTOS}
            onChange={(event) => {
              void pick(event.target.files)
              // Сбрасываем поле: иначе повторный выбор того же файла не даст события change.
              event.target.value = ''
            }}
          />

          {photoCount >= MAX_PHOTOS && (
            <p className="text-muted">
              <small>Больше {MAX_PHOTOS} фото нельзя.</small>
            </p>
          )}

          {adId === null && pending.length > 0 && (
            <p className="text-muted">
              <small>Фото загрузятся, как только объявление будет опубликовано.</small>
            </p>
          )}
        </div>

        <button type="submit" className="btn btn-success" disabled={busy}>
          {busy ? 'Сохраняем…' : 'Сохранить'}
        </button>{' '}
        <button type="button" className="btn btn-default" onClick={onClose}>
          Отмена
        </button>
      </form>
    </Modal>
  )
}

/**
 * Миниатюра ещё НЕ отправленного фото: показываем прямо из файла на диске у человека.
 * Ссылку на blob освобождаем при размонтировании — иначе браузер держит байты в памяти.
 */
export function LocalThumb({ file, onRemove }: { file: File; onRemove: () => void }) {
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    const objectUrl = URL.createObjectURL(file)
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [file])

  return (
    <span style={{ display: 'inline-block', position: 'relative', marginRight: 8, marginBottom: 8 }}>
      <img
        src={url ?? undefined}
        alt=""
        style={{
          width: 80,
          height: 80,
          objectFit: 'cover',
          border: '1px dashed #999',   // пунктир: фото ещё не на сервере
        }}
      />
      <button
        type="button"
        className="btn btn-danger btn-xs"
        style={{ position: 'absolute', top: 2, right: 2, padding: '0 5px' }}
        onClick={onRemove}
        title="Убрать фото"
      >
        ×
      </button>
    </span>
  )
}

/** Миниатюра загруженного фото с крестиком. */
export function PhotoThumb({ photoId, onRemove }: { photoId: number; onRemove: () => void }) {
  const url = useAuthImage(`/board/photo/${photoId}`)

  return (
    <span style={{ display: 'inline-block', position: 'relative', marginRight: 8, marginBottom: 8 }}>
      <img
        src={url ?? undefined}
        alt=""
        style={{ width: 80, height: 80, objectFit: 'cover', border: '1px solid #ddd' }}
      />
      <button
        type="button"
        className="btn btn-danger btn-xs"
        style={{ position: 'absolute', top: 2, right: 2, padding: '0 5px' }}
        onClick={onRemove}
        title="Удалить фото"
      >
        ×
      </button>
    </span>
  )
}
